import { twJoin } from '@tuic/tailwind-config';

import { Button } from '../../../core-components/Button';

import { DashboardStageOverviewHeader } from './components/Header';
import { DashboardStageOverviewImage } from './components/Image';
import { DashboardStageOverviewTable } from './components/Table';
import { DashboardStageOverviewTitle } from './components/Title';

import type { ButtonProps } from '../../../core-components/Button';
import type { DashboardStageOverviewCountdown } from './components/Countdown';
import type { TableItem } from './components/Table';
import type { ComponentPropsWithoutRef, FC, ReactNode } from 'react';

export type DashboardStageOverviewProps = {
  fallback?: boolean;

  temperature?: number;
  cabinNumber?: string;
  bookingCode?: string;

  mainImage?: ReactNode;
  highlightImage?: ReactNode;
  countdown?: ComponentPropsWithoutRef<typeof DashboardStageOverviewCountdown>;

  headline?: ReactNode;
  nextHeadline?: ReactNode;
  subline?: ReactNode;
  description?: ReactNode;

  buttonTripPlan?: ButtonProps;
  buttonNextTrip?: ButtonProps;
  buttonHighlight?: ButtonProps;
  buttonItinerary?: ButtonProps;
  buttonTripDetails?: ButtonProps;
  buttonDailyProgram?: ButtonProps;

  tableItems?: TableItem[];

  cabinNumberText?: string;
  bookingCodeText?: string;
};

/**
 * The `DashboardStageOverview` component displays an overview section for a trip dashboard, including images, headlines, countdowns, action buttons, and a table of details.
 *
 * ## Props
 *
 * - `fallback`: Optional boolean to render fallback UI.
 * - `temperature`: Optional number representing the temperature.
 * - `cabinNumber`: Optional string for the cabin number.
 * - `bookingCode`: Optional string for the booking code.
 * - `mainImage`: Main image for the stage (ReactNode).
 * - `highlightImage`: Highlight image for the stage (ReactNode).
 * - `countdown`: Optional countdown configuration (props for `StageCountdown`).
 * - `headline`: Optional main headline (ReactNode).
 * - `nextHeadline`: Optional headline for the next stage (ReactNode).
 * - `subline`: Optional subline text (ReactNode).
 * - `description`: Optional description text (ReactNode).
 * - `buttonTripPlan`: Optional ButtonProps for the trip plan button.
 * - `buttonNextTrip`: Optional ButtonProps for the next trip button.
 * - `buttonHighlight`: Optional ButtonProps for the highlight button.
 * - `buttonItinerary`: Optional ButtonProps for the itinerary button.
 * - `buttonTripDetails`: Optional ButtonProps for the trip details button.
 * - `tableItems`: Optional array of items to display in the table.
 * - `cabinNumberText`: Optional text label for the cabin number.
 * - `bookingCodeText`: Optional text label for the booking code.
 *
 * ```tsx
 * <DashboardStageOverview
 *   temperature={22}
 *   cabinNumber="1023"
 *   bookingCode="ABC123"
 *   mainImage={<img src="/img.jpg" alt="Stage" />}
 *   highlightImage={<img src="/highlight.jpg" alt="Highlight" />}
 *   headline="Willkommen an Bord"
 *   subline="Ihre Reise beginnt"
 *   description="Genießen Sie Ihren Aufenthalt."
 *   buttonTripPlan={<Button>Trip Plan</Button>}
 *   buttonTripDetails={<Button>Details</Button>}
 *   tableItems={[{ label: 'Abfahrt', value: '10:00' }]}
 * />
 * ```
 */
export const DashboardStageOverview: FC<DashboardStageOverviewProps> = ({
  fallback,

  temperature,
  cabinNumber,
  bookingCode,

  mainImage,
  highlightImage,
  countdown,

  headline,
  nextHeadline,
  subline,
  description,

  buttonTripPlan,
  buttonNextTrip,
  buttonHighlight,
  buttonItinerary,
  buttonTripDetails,
  buttonDailyProgram,

  tableItems,

  cabinNumberText,
  bookingCodeText,
}) => {
  return (
    <div className={twJoin('flex flex-col gap-4 text-marine-high-emphasis')}>
      <div className="flex flex-col gap-3">
        <DashboardStageOverviewHeader
          fallback={fallback}
          temperature={temperature}
          cabinNumber={cabinNumber}
          bookingCode={bookingCode}
          cabinNumberText={cabinNumberText}
          bookingCodeText={bookingCodeText}
        />
        <DashboardStageOverviewImage
          mainImage={mainImage}
          buttonHighlight={buttonHighlight}
          highlightImage={highlightImage}
          buttonItinerary={buttonItinerary}
          countdown={countdown}
          fallback={fallback}
        />
      </div>
      <DashboardStageOverviewTitle
        title={headline}
        nextTitle={nextHeadline}
        subTitle={subline}
        description={description}
        buttonItinerary={buttonItinerary}
        fallback={fallback}
      />
      <div className="hidden gap-4 lg:flex">
        {!!buttonTripPlan && <Button {...buttonTripPlan} on="white" />}
        {!!buttonTripDetails && <Button {...buttonTripDetails} on="white" />}
        {!!buttonNextTrip && <Button {...buttonNextTrip} on="white" />}
        {!!buttonDailyProgram && (
          <Button {...buttonDailyProgram} on="white" />
        )}
      </div>
      <DashboardStageOverviewTable items={tableItems} fallback={fallback} />
      <div className="flex flex-col gap-4 md:flex-row md:gap-6 lg:hidden">
        {!!buttonTripPlan && <Button {...buttonTripPlan} on="white" />}
        {!!buttonHighlight && (
          <Button {...buttonHighlight} on="white" variant="secondary" />
        )}
        {!!buttonDailyProgram && (
          <Button {...buttonDailyProgram} on="white" variant="secondary" />
        )}
      </div>
    </div>
  );
};
